import { Box, Collapse, Grid, Slide, Typography, useTheme } from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { ActivityIndicator, Animated, Image, ImageBackground, ScrollView, StyleSheet, View, Platform } from "react-native";
import { Categories, productStore } from "../../../app/stores/Product";
import { ProductsLayout } from "../../../components/ProductsLayout";
import { ProductsService } from "../../../services/supabase/ProductsService";
import { CategoriesFilterScroller, CategoriesFilterSideMenu } from "./components/CategoryFilters";
import { ProductsToolbar } from "./components/ProductsToolbar";
import { useStoreConfig } from "../../../contexts/StoreConfigContext";
import { navigationStore } from "../../../app/stores/Navigation";
import { useIsFocused } from "@react-navigation/native";
import { Footer } from "../../../components/enhancedfooter/Footer";
import { appConfigStore } from "../../../app/stores/AppConfig";
import { useTracking } from "../../../app/hooks/custom/useTracking";
import { getActiveProductsLength, filterProductsByCategoryAndSubcategory, getSubcategoriesForCategory } from "../../../utils/customFunctions";
import { useComponentMount } from "../../../services/InitializationService";


export const Shop = ({ navigation, route }) => {
	// Debug component mounting
	useComponentMount('Shop');

	// Track page view
	const { trackPageView } = useTracking();
	const productsService = ProductsService();
	const [categoriesAnimation] = useState(new Animated.Value(0));
	const [introImageAnimation] = useState(new Animated.Value(0));
	const [productsAnimation] = useState(new Animated.Value(0));
	const [marginTopAnimation] = useState(new Animated.Value(0));
	const [selectedCategories, setSelectedCategories] = useState<Categories[]>([]);
	const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
	const [sortOrder, setSortOrder] = useState<'price_asc' | 'price_desc' | null>(null);
	const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
	const [gridDensity, setGridDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
	const [categoriesTransition, setCategoriesTransition] = useState(false);

	const { updateShowNavigationBar } = navigationStore((state) => state);
	const { allProducts, allCategories } = productStore((state) => state);
	const darkMode = appConfigStore((state) => state.darkMode);
	const { config } = useStoreConfig();

	const paddingX = 4;
	const scrollViewRef = useRef<ScrollView>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [_isCategoryChanging, setIsCategoryChanging] = useState(false);
	const isFocused = useIsFocused();
	const theme = useTheme();
	const [_imagePreloaded, setImagePreloaded] = useState(false);
	const params = route?.params;

	const isAllCategory = useMemo(() => {
		if (selectedCategories.length === 0) return false;
		return typeof selectedCategories[0] === "string"
			? selectedCategories[0] === "All"
			: selectedCategories[0].category === "All";
	}, [selectedCategories]);

	// Animate margin top when category changes
	useEffect(() => {
		Animated.timing(marginTopAnimation, {
			toValue: isAllCategory ? 1 : 0,
			duration: 600,
			useNativeDriver: false
		}).start();
	}, [isAllCategory, marginTopAnimation]);

	useEffect(() => {
		scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
	}, [selectedCategories])


	useEffect(() => {
		// Track page view only once
		trackPageView('Shop', '/shop').catch(err => console.error('Track error:', err));
		productsService.getAllProducts();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isFocused) {
			updateShowNavigationBar(true);
		}
	}, [isFocused]);

	useEffect(() => {
		if (allCategories.length > 0) {
			// Check if a category was passed via route params
			if (params?.category) {
				const categoryFromParam = allCategories.find(
					cat => cat.category.toLowerCase() === params.category.toLowerCase()
				);
				if (categoryFromParam) {
					setSelectedCategories([categoryFromParam.category as unknown as Categories]);
				} else {
					setSelectedCategories([allCategories[0].category as unknown as Categories]);
				}
			} else {
				setSelectedCategories([allCategories[0].category as unknown as Categories]);
			}
			fadeIn();
		}
	}, [allCategories, params?.category]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setCategoriesTransition(isAllCategory);
		}, 300);

		return () => clearTimeout(timer);
	}, [isAllCategory]);

	const handleCategoryChange = useCallback((categories: Categories[]) => {
		setIsCategoryChanging(true);
		setSelectedCategories(categories);
	}, []);

	useEffect(() => {
		try {
			if (Platform.OS === "web") {
				const img = new window.Image();
				img.onload = () => setImagePreloaded(true);
				img.onerror = () => {
					console.error("Failed to preload image on web");
					setImagePreloaded(true);
				};
				img.src = require("../../../assets/bg-texture-paint.jpg");
			} else {
				Image.prefetch(Image.resolveAssetSource(require("../../../assets/bg-texture-paint.jpg")).uri)
					.then(() => setImagePreloaded(true))
					.catch(error => {
						console.error("Failed to preload image:", error);
						setImagePreloaded(true);
					});
			}
		} catch (error) {
			console.error("Failed to preload image:", error);
			setImagePreloaded(true);
		}
	}, []);

	const fadeIn = useCallback(() => {
		return Animated.timing(categoriesAnimation, {
			toValue: 1,
			duration: 600,
			delay: 250,
			useNativeDriver: false,
		}).start(() => {
			setIsLoading(false);
			Animated.timing(introImageAnimation, {
				toValue: 1,
				duration: 600,
				delay: 0,
				useNativeDriver: false,
			}).start(() => {
				Animated.timing(productsAnimation, {
					toValue: 1,
					duration: 600,
					delay: 0,
					useNativeDriver: false,
				}).start();
			});
		});
	}, [categoriesAnimation, introImageAnimation, productsAnimation]);

	const getProductsLength = useCallback((category) => {
		return getActiveProductsLength(allProducts, category);
	}, [allProducts]);

	// Derive subcategories for the currently selected category
	const selectedCategoryName = useMemo(() => {
		if (selectedCategories.length === 0) return 'All';
		const cat = selectedCategories[0];
		return typeof cat === 'string' ? cat : (cat as any).category;
	}, [selectedCategories]);

	// Subcategories for the selected category (used by mobile chip row)
	const availableSubcategories = useMemo(() => {
		return getSubcategoriesForCategory(allProducts, selectedCategoryName);
	}, [allProducts, selectedCategoryName]);

	// Max price for the current category — resets price range when category changes
	const maxPriceForCategory = useMemo(() => {
		const relevantProducts = selectedCategoryName === 'All'
			? allProducts.filter(p => p.isActive !== false)
			: allProducts.filter(p => p.category === selectedCategoryName && p.isActive !== false);
		const prices = relevantProducts.map(p => p.price).filter(p => p > 0);
		return prices.length > 0 ? Math.ceil(Math.max(...prices)) : 0;
	}, [allProducts, selectedCategoryName]);

	useEffect(() => {
		if (maxPriceForCategory > 0) {
			setPriceRange([0, maxPriceForCategory]);
		}
	}, [maxPriceForCategory]);

	// Subcategories for ALL categories (used by desktop sidebar for always-visible nesting)
	const allSubcategoriesByCategory = useMemo(() => {
		const result: Record<string, { subcategory: string; totalProducts: number }[]> = {};
		allCategories.forEach(cat => {
			if (cat.category === 'All') return;
			const subcats = getSubcategoriesForCategory(allProducts, cat.category);
			if (subcats.length > 0) {
				result[cat.category] = subcats;
			}
		});
		return result;
	}, [allProducts, allCategories]);

	const handleCategoryChangeWithSubcategoryReset = useCallback((categories: Categories[]) => {
		setSelectedSubcategory(null);
		handleCategoryChange(categories);
	}, [handleCategoryChange]);

	const CategoryProducts = useMemo(() => ({ category }) => {
		const filteredProducts = useMemo(() => {
		let products = filterProductsByCategoryAndSubcategory(allProducts, category, selectedSubcategory);
		const [minP, maxP] = priceRange;
		if (maxPriceForCategory > 0 && (minP > 0 || maxP < maxPriceForCategory)) {
			products = products.filter(p => p.price >= minP && p.price <= maxP);
		}
		if (sortOrder === 'price_asc') return [...products].sort((a, b) => a.price - b.price);
		if (sortOrder === 'price_desc') return [...products].sort((a, b) => b.price - a.price);
		return products;
	}, [category, allProducts, selectedSubcategory, sortOrder, priceRange, maxPriceForCategory]);

		return (
			<React.Fragment key={category}>
				<Animated.View style={{
					alignItems: "center",
					opacity: productsAnimation
				}}>
					<Grid
						container
						flexDirection="row"
						maxWidth={"xl"}
						sx={{
							paddingX: { xs: 2, md: paddingX },
							alignContent: { xs: "center" },
						}}
						marginTop={4}
						spacing={1}
					>
					<CategoriesFilterSideMenu
						scrollViewRef={scrollViewRef}
						allCategories={allCategories}
						selectedCategories={selectedCategories}
						setSelectedCategories={handleCategoryChange}
						getProductsLength={getProductsLength}
						allSubcategoriesByCategory={allSubcategoriesByCategory}
						selectedSubcategory={selectedSubcategory}
						setSelectedSubcategory={setSelectedSubcategory}
						priceRange={priceRange}
						setPriceRange={setPriceRange}
						maxPrice={maxPriceForCategory}
					/>
						<Grid
							item
							xs={12}
							md={9}
							sx={{
								flexGrow: 1
							}}
						>
							<ProductsToolbar
								productCount={filteredProducts.length}
								sortOrder={sortOrder}
								setSortOrder={setSortOrder}
								gridDensity={gridDensity}
								setGridDensity={setGridDensity}
							/>
							<ProductsLayout
								selectedCategory={category}
								products={filteredProducts}
								navigation={navigation}
								onLoadingComplete={() => setIsCategoryChanging(false)}
								gridDensity={gridDensity}
							/>
						</Grid>
					</Grid>
				</Animated.View>
			</React.Fragment >
		);
	}, [allCategories, selectedCategories, getProductsLength, allProducts, navigation, productsAnimation, handleCategoryChange, allSubcategoriesByCategory, selectedSubcategory, setSelectedSubcategory, handleCategoryChangeWithSubcategoryReset, sortOrder, setSortOrder, gridDensity, setGridDensity, priceRange, setPriceRange, maxPriceForCategory]);

	// Note: Header image caching removed - browser HTTP cache handles this more efficiently
	// Storing base64 images in localStorage causes quota exceeded errors

	const IntroImage = useMemo(() => () => {
		const headerMediaUrl = config.appHeaderImage;
		const headerMediaType = config.appHeaderMediaType || 'image';
		const headerPosition = config.appHeaderPosition || { x: 50, y: 50 };
		const headerScale = config.appHeaderScale || 1.0;
		const headerObjectFit = config.appHeaderObjectFit || 'cover';
		const overlay = config.headerOverlay;
		const isVideo = headerMediaType === 'video';

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const videoRef = useRef<HTMLVideoElement>(null);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (videoRef.current) {
				videoRef.current.muted = true;
			}
		}, []);

		const isCustomMedia = typeof headerMediaUrl === 'string' && (
			headerMediaUrl.startsWith('http://') ||
			headerMediaUrl.startsWith('https://')
		);

		// Use URL directly - browser HTTP cache handles caching efficiently
		const imageSource = isCustomMedia && !isVideo
			? { uri: headerMediaUrl }
			: require('../../../assets/bg-texture-paint.jpg');

		// Get font family for overlay
		const getOverlayFontFamily = () => {
			return overlay.font ? `'${overlay.font}', sans-serif` : 'logoFont';
		};

		// Get text shadow CSS
		const getTextShadow = () => {
			if (!overlay.textShadow.enabled) return 'none';
			return `${overlay.textShadow.offsetX}px ${overlay.textShadow.offsetY}px ${overlay.textShadow.blur}px ${overlay.textShadow.color}`;
		};

		// Get vignette styles
		const getVignetteSx = () => {
			if (!overlay.vignette.enabled) return {};
			
			const intensity = overlay.vignette.intensity;
			const color = overlay.vignette.color;
			const alpha = Math.round(intensity * 255).toString(16).padStart(2, '0');
			
			return {
				'&::before': {
					content: '""',
					position: 'absolute',
					inset: 0,
					background: `radial-gradient(ellipse at center, transparent 20%, ${color}${alpha} 100%)`,
					pointerEvents: 'none',
					zIndex: 1,
				}
			};
		};

		// Text overlay component (shared between image and video)
		const TextOverlay = () => overlay.enabled && overlay.text ? (
			<Box
				sx={{
					position: 'absolute',
					inset: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: overlay.textAlign === 'left'
						? 'flex-start'
						: overlay.textAlign === 'right'
							? 'flex-end'
							: 'center',
					px: { xs: 2, md: 6 },
					transform: `translate(${(overlay.position.x - 50) * 0.5}%, ${(overlay.position.y - 50) * 0.5}%)`,
					zIndex: 2,
				}}
			>
				<Typography
					sx={{
						fontFamily: getOverlayFontFamily(),
						fontSize: {
							xs: `${overlay.fontSize * 0.4}rem`,
							sm: `${overlay.fontSize * 0.6}rem`,
							md: `${overlay.fontSize}rem`
						},
						fontWeight: overlay.fontWeight,
						color: overlay.color,
						letterSpacing: `${overlay.letterSpacing}px`,
						textShadow: getTextShadow(),
						textAlign: overlay.textAlign,
						whiteSpace: 'pre-wrap',
						lineHeight: 1.2,
					}}
				>
					{overlay.text}
				</Typography>
			</Box>
		) : null;

		return (
			<Slide
					direction='down'
					in={isAllCategory}
					timeout={{
						enter: 600,
						exit: 600
					}}
					mountOnEnter
					unmountOnExit
				>
					<div>
						<Collapse
							mountOnEnter
							unmountOnExit
							easing={{
								enter: 'ease-in-out',
								exit: 'ease-in-out'
							}}
							in={isAllCategory}
							timeout={'auto'}
							sx={{ backgroundColor: darkMode ? theme.customColors.background : theme.customColors.cardBackground }}
						>
							<Grid
								container
								marginTop={4}
								marginBottom={3}
							>
								<Grid
									item
									flex={1}
									marginX={{ xs: 2, md: paddingX }}
									boxShadow={'0px 8px 10px rgba(0, 0, 0, 0.1)'}
									borderRadius={4}
									overflow={'hidden'}
									sx={{
										aspectRatio: { xs: 1 / 0.38, sm: 1 / 0.30, md: 1 / 0.22, },
										position: 'relative',
										'& img, & video': {
											objectPosition: `${headerPosition.x}% ${headerPosition.y}%`,
											transform: `scale(${headerScale})`,
										},
										...getVignetteSx(),
									}}
								>
								{isVideo && isCustomMedia ? (
									// Video header
									<Box sx={{ width: '100%', height: '100%', position: 'relative', }}>
										<video
										ref={videoRef}
										key={headerMediaUrl}
										src={headerMediaUrl}
										autoPlay
										muted
										loop
										playsInline
										onError={(e) => console.error('Header video failed to load:', e)}
											style={{
												width: '100%',
												height: '100%',
												objectFit: headerObjectFit,
												objectPosition: `${headerPosition.x}% ${headerPosition.y}%`,
												transform: `scale(${headerScale})`,
											}}
										/>
										<TextOverlay />
									</Box>
								) : (
								// Image header (default)
										<ImageBackground
											source={imageSource}
											resizeMode='cover'
											style={{
												width: '100%',
												height: '100%',
												backgroundColor: theme.customColors.background,
												justifyContent: 'center',
											}}
										>
										<TextOverlay />
									</ImageBackground>
								)}
								</Grid>
							</Grid>
						</Collapse>
					</div>
				</Slide>
		);
	}, [isAllCategory, paddingX, config.appHeaderImage, config.appHeaderMediaType, config.appHeaderPosition, config.appHeaderScale, config.appHeaderObjectFit, config.headerOverlay, darkMode, theme.customColors]);

	return (
		<>
			{isLoading ? (
				<View style={{
					flex: 1,
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: theme.customColors.background
				}}>
					<ActivityIndicator
						size="large"
						color={config.primaryColor}
					/>
				</View>
			) : (
					<Animated.ScrollView
						ref={scrollViewRef}
						style={{ backgroundColor: theme.customColors.background }}
						contentContainerStyle={styles.background}
						stickyHeaderIndices={[1]}
						stickyHeaderHiddenOnScroll={false}
						scrollEventThrottle={16}
						scrollToOverflowEnabled={true}>
						<IntroImage />
						<div style={{
							position: 'relative',
							zIndex: 10,
							transition: 'opacity 700ms cubic-bezier(0.4, 0, 0.2, 1), margin 700ms cubic-bezier(0.4, 0, 0.2, 1)',
							marginTop: categoriesTransition ? '0px' : '-4px',
						}}>
							<CategoriesFilterScroller
								scrollViewRef={scrollViewRef}
								allCategories={allCategories}
								selectedCategories={selectedCategories}
								setSelectedCategories={handleCategoryChangeWithSubcategoryReset}
								getProductsLength={getProductsLength}
								availableSubcategories={availableSubcategories}
								selectedSubcategory={selectedSubcategory}
								setSelectedSubcategory={setSelectedSubcategory}
								sortOrder={sortOrder}
								setSortOrder={setSortOrder}
							/>
						</div>
						{/* Products container */}
						<Animated.View style={{
							...styles.container,
							backgroundColor: theme.customColors.background,
							marginTop: marginTopAnimation.interpolate({
								inputRange: [0, 1],
								outputRange: [-20, 0]
							})
						}}>
							{selectedCategories.map((category, index) => (
								<CategoryProducts
									key={`${category}-${index}`}
									category={category}
								/>
							))}
						</Animated.View>
						<Footer />
					</Animated.ScrollView>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	background: {
		backgroundColor: "transparent",
	},
	container: {
		flex: 1,
		backgroundColor: "transparent",
	},
	title: {
		flex: 1,
		fontSize: 26,
		fontWeight: "600",
		fontFamily: "primaryFont",
		letterSpacing: 1,
		color: "black",
	},
	image: {
		marginTop: 20,
		width: 50,
		height: 50,
		resizeMode: "contain",
		alignSelf: "center",
	},
	headerTitle: {
		flex: 1,
		fontSize: 48,
		fontWeight: "800",
		fontFamily: "primaryFont",
		letterSpacing: 1.5,
		color: "white",
	},
	categoryTitle: {
		fontSize: 48,
		fontWeight: "800",
		fontFamily: "primaryFont",
		letterSpacing: 1.5,
		color: "white",
	},
	chipContainer: {
		flexDirection: "row",
		flexWrap: "wrap",
		marginTop: 10,
	},
	chip: {
		letterSpacing: 0.5,
		fontFamily: "primaryFont",
		fontWeight: "600",
		minHeight: 40,
		marginRight: 8,
		borderRadius: 20,
		borderWidth: 2,
	},
});